<script setup>
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const dialogRef = ref(null);
const ticketsDialogRef = ref(null);
const ticketsInfo = ref([]);
const totalTickets = ref(0);
const pendingContactId = ref(null);

const checkAndOpenDialog = async () => {
  const contactId = route.params.contactId || props.selectedContact?.id;
  if (!contactId) return;

  try {
    const response = await ContactAPI.checkTickets(contactId);

    if (response.data.has_tickets) {
      ticketsInfo.value = response.data.tickets || [];
      totalTickets.value = response.data.total_tickets || 0;
      pendingContactId.value = contactId;
      ticketsDialogRef.value?.open();
    } else {
      dialogRef.value?.open();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const deleteContact = async (id, forceDelete = false) => {
  if (!id) return;

  try {
    await store.dispatch('contacts/delete', { id, forceDelete });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.SUCCESS_MESSAGE'));
    emit('goToContactsList');
    dialogRef.value?.close();
    ticketsDialogRef.value?.close();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleDialogConfirm = async () => {
  const contactId = route.params.contactId || props.selectedContact.id;
  await deleteContact(contactId, false);
};

const handleForceDelete = async () => {
  if (pendingContactId.value) {
    await deleteContact(pendingContactId.value, true);
    pendingContactId.value = null;
  }
};

const handleCancelForceDelete = () => {
  ticketsDialogRef.value?.close();
  pendingContactId.value = null;
  ticketsInfo.value = [];
  totalTickets.value = 0;
};

const getTicketsListText = () => {
  if (ticketsInfo.value.length === 0) return '';

  const ticketsList = ticketsInfo.value
    .map(ticket => `• ${ticket.title} (ID: ${ticket.id})`)
    .join('\n');

  if (totalTickets.value > ticketsInfo.value.length) {
    const moreCount = totalTickets.value - ticketsInfo.value.length;
    return `${ticketsList}\n${t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TICKETS_DIALOG.MORE_TICKETS', { count: moreCount })}`;
  }

  return ticketsList;
};

const getTicketsDescription = () => {
  const plural = totalTickets.value !== 1 ? 's' : '';
  return t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TICKETS_DIALOG.DESCRIPTION', {
    count: totalTickets.value,
    plural,
  });
};

defineExpose({ dialogRef, checkAndOpenDialog });
</script>

<template>
  <div>
    <Dialog
      ref="dialogRef"
      type="alert"
      :title="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TITLE')"
      :description="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.DESCRIPTION')"
      :confirm-button-label="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.CONFIRM')"
      @confirm="handleDialogConfirm"
    />

    <Dialog
      ref="ticketsDialogRef"
      type="alert"
      :title="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TICKETS_DIALOG.TITLE')"
      :confirm-button-label="
        t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TICKETS_DIALOG.CONFIRM')
      "
      @confirm="handleForceDelete"
      @cancel="handleCancelForceDelete"
    >
      <template #description>
        <div class="flex flex-col gap-3">
          <p class="text-sm text-n-slate-11" v-html="getTicketsDescription()" />
          <div class="bg-n-slate-2 rounded-md p-3 max-h-48 overflow-y-auto">
            <pre
              class="text-xs whitespace-pre-wrap text-n-slate-12 font-mono"
              >{{ getTicketsListText() }}</pre>
          </div>
          <p
            class="text-sm text-n-slate-11"
            v-html="
              t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TICKETS_DIALOG.WARNING')
            "
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
